import { createSlice } from '@reduxjs/toolkit';
import { normalizeRole } from '../../utils/authRoutes';
import { clearAuthSession, setAuthSession } from '../../utils/authSession';

function readStoredUser() {
    try {
        const raw = localStorage.getItem('user');
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

const storedUser = readStoredUser();
const storedRole = normalizeRole(storedUser?.role);

const initialState = {
    status: 'idle',
    userDetails: [],
    tempDetails: [],
    loading: false,
    currentUser: storedUser,
    currentRole: storedRole,
    error: null,
    response: null,
    darkMode: true
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        authRequest: (state) => {
            state.status = 'loading';
        },
        underControl: (state) => {
            state.status = 'idle';
            state.response = null;
            state.error = null;
        },
        stuffAdded: (state, action) => {
            state.status = 'added';
            state.response = null;
            state.error = null;
            state.tempDetails = action.payload;
        },
        authSuccess: (state, action) => {
            state.status = 'success';
            const prev = state.currentUser || {};
            const nextId = action.payload?._id || action.payload?.id;
            const prevId = prev._id || prev.id;
            const sameAccount =
                nextId && prevId && String(nextId) === String(prevId);
            const staffRole =
                action.payload?.staffRole ??
                prev.staffRole ??
                (action.payload?.role && action.payload.role !== "Teacher"
                    ? action.payload.role
                    : prev.staffRole);
            state.currentUser = sameAccount
                ? { ...prev, ...action.payload, staffRole }
                : { ...action.payload, staffRole };
            state.currentRole = normalizeRole(action.payload?.role) || action.payload?.role || state.currentRole || null;
            localStorage.setItem('user', JSON.stringify({
                ...state.currentUser,
                role: state.currentRole || action.payload?.role,
                staffRole,
            }));
            setAuthSession(state.currentUser);
            state.response = null;
            state.error = null;
            state.loading = false;
        },
        authFailed: (state, action) => {
            state.status = 'failed';
            state.response = action.payload;
            state.loading = false;
        },
        authError: (state, action) => {
            state.status = 'error';
            state.error = action.payload;
        },
        authLogout: (state) => {
            localStorage.removeItem('user');
            clearAuthSession();
            state.currentUser = null;
            state.status = 'idle';
            state.error = null;
            state.currentRole = null
        },

        doneSuccess: (state, action) => {
            state.userDetails = action.payload;
            state.loading = false;
            state.error = null;
            state.response = null;
        },
        getDeleteSuccess: (state) => {
            state.loading = false;
            state.error = null;
            state.response = null;
        },

        getRequest: (state) => {
            state.loading = true;
        },
        getFailed: (state, action) => {
            state.response = action.payload;
            state.loading = false;
            state.error = null;
        },
        getError: (state, action) => {
            state.loading = false;
            const p = action.payload;
            if (typeof p === 'string') {
                state.error = p;
            } else if (p?.response?.data?.message) {
                state.error = p.response.data.message;
            } else if (p?.message) {
                state.error = String(p.message);
            } else {
                state.error = 'Request failed';
            }
        },
        toggleDarkMode: (state) => {
            state.darkMode = !state.darkMode;
        },
        patchCurrentUser: (state, action) => {
            if (!state.currentUser) return;
            const prev = state.currentUser;
            const staffRole =
                action.payload?.staffRole ??
                prev.staffRole ??
                (action.payload?.role && action.payload.role !== "Teacher"
                    ? action.payload.role
                    : prev.staffRole);
            state.currentUser = { ...prev, ...action.payload, staffRole };
            localStorage.setItem("user", JSON.stringify(state.currentUser));
            setAuthSession(state.currentUser);
        },
    },
});

export const {
    authRequest,
    underControl,
    stuffAdded,
    authSuccess,
    authFailed,
    authError,
    authLogout,
    doneSuccess,
    getDeleteSuccess,
    getRequest,
    getFailed,
    getError,
    toggleDarkMode,
    patchCurrentUser,
} = userSlice.actions;

export const userReducer = userSlice.reducer;
